import type { CurrentFiltersType } from "@/types/common/filters";
import {
  useQuery,
  keepPreviousData,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { PaginatedResponse } from "./data-center.api";
import axios from "@/utils/axios";

export type TableColumnType = "STRING" | "INTEGER" | "BOOLEAN";

export type TableRow = {
  id: string;
  "row-key": string;
  [key: string]: string | number | boolean;
};

export interface TableColumn {
  id: string;
  name: string;
  type: TableColumnType;
  nullable?: boolean;
}

export interface Table {
  id: string;
  name: string;
  description: string;
  columns: TableColumn[];
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  totalRows: number;
}

export interface GetFCTablesParams {
  page?: number;
  per_page?: number;
  filters?: CurrentFiltersType[];
  order_by?: string;
  orderDirection: string;
  search?: string;
  created_at?: string;
  updated_at?: string;
  role_id?: string;
}

/**
 * Add the local index column
 *
 * @param table
 * @returns
 */
const processTable = (columns: TableColumn[]): TableColumn[] => {
  if (!columns) return columns;
  columns = columns.map((col) => ({ ...col, id: col.name }));
  const indexCol = {
    id: "local_index_col",
    name: "INDEX",
    type: "int8" as TableColumnType,
    nullable: false,
  };
  if (columns.length > 0) {
    columns.unshift(indexCol);
  } else {
    columns = [indexCol];
  }
  return columns;
};

const processData = (data: TableRow[]): TableRow[] => {
  return data.map((row, index) => {
    return {
      ...row,
      local_index_col: index + 1,
    };
  });
};

/**
 * Get core functional table list
 *
 * @param params: GetFCTablesParams
 * @returns
 */
export const useGetFCTables = () => {
  return useQuery({
    // Exclude params since filters are locally supported due missing CF data
    // queryKey: ["data-center-fc", "tables", params]
    queryKey: ["data-center-fc", "tables"],
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PaginatedResponse<Table>> => {
      const response = await axios.get(
        `/admin/v1/data-center/functional-core/tables`,
      );
      return response.data;
    },
  });
};

/**
 * Get the core functional table detail
 *
 * @param id
 * @returns
 */
export const useGetFCTableDetail = (
  name: string | undefined,
): UseQueryResult<TableColumn[]> => {
  return useQuery({
    queryKey: ["data-center-fc", "table", name?.replace(/\s+/g, "-")],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await axios.get(
        `/admin/v1/data-center/functional-core/columns`,
        {
          params: {
            table_name: name,
          },
        },
      );
      return processTable(response.data.data);
    },
    enabled: !!name,
  });
};

/**
 * Get the core functional table rows (data)
 *
 * @param name
 * @returns
 */
export const useGetFCTableData = (name: string) => {
  return useQuery({
    queryKey: ["data-center", "table-data", name],
    queryFn: async () => {
      const data = await axios.get(
        "/admin/v1/data-center/functional-core/data",
        {
          params: {
            table_name: name,
          },
        },
      );
      return processData(data?.data?.data as TableRow[]) ?? [];
    },
    enabled: !!name,
  });
};
